/** Pure binary selectors for Garden's monotonic virtual layout/geometry. */

export interface GardenVirtualMetrics {
  comparisons: number;
}

export interface PositionedGardenItem {
  top: number;
  height: number;
}

export interface GardenVisibleRange {
  start: number;
  end: number;
}

function compare(metrics?: GardenVirtualMetrics): void {
  if (metrics) metrics.comparisons += 1;
}

/** Half-open range intersecting [minimumTop, maximumBottom]. */
export function gardenVisibleRange<T extends PositionedGardenItem>(
  items: readonly T[],
  minimumTop: number,
  maximumBottom: number,
  metrics?: GardenVirtualMetrics,
): GardenVisibleRange {
  let low = 0;
  let high = items.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    compare(metrics);
    if (items[middle].top + items[middle].height < minimumTop) low = middle + 1;
    else high = middle;
  }
  const start = low;
  high = items.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    compare(metrics);
    if (items[middle].top <= maximumBottom) low = middle + 1;
    else high = middle;
  }
  return { start, end: low };
}

/** Half-open index range for a monotonic coordinate array. */
export function gardenCoordinateRange<T>(
  items: readonly T[],
  coordinate: (item: T) => number,
  minimum: number,
  maximum: number,
  metrics?: GardenVirtualMetrics,
): GardenVisibleRange {
  let low = 0;
  let high = items.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    compare(metrics);
    if (coordinate(items[middle]) < minimum) low = middle + 1;
    else high = middle;
  }
  const start = low;
  high = items.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    compare(metrics);
    if (coordinate(items[middle]) <= maximum) low = middle + 1;
    else high = middle;
  }
  return { start, end: low };
}

/** Slice a monotonic coordinate array to inclusive [minimum, maximum]. */
export function gardenCoordinateSlice<T>(
  items: readonly T[],
  coordinate: (item: T) => number,
  minimum: number,
  maximum: number,
  metrics?: GardenVirtualMetrics,
): T[] {
  const range = gardenCoordinateRange(items, coordinate, minimum, maximum, metrics);
  return items.slice(range.start, range.end);
}

/** Last monotonic item whose top is at or before `position`. */
export function gardenLastAtOrBefore<T extends { top: number }>(
  items: readonly T[],
  position: number,
  metrics?: GardenVirtualMetrics,
): T | null {
  let low = 0;
  let high = items.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    compare(metrics);
    if (items[middle].top <= position) low = middle + 1;
    else high = middle;
  }
  return low === 0 ? null : items[low - 1];
}
